var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useDrag, useDrop } from 'react-dnd';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import Icon from 'ziphy-web-shared/basic/assets/images/Icon';
import { getUuid, nativeScrollTo } from 'ziphy-web-shared/basic/helpers';
import { useLoaderSubscribe } from 'ziphy-web-shared/basic/utils';
import styles from './BlockOrderWrapper.module.scss';
const BlockOrderWrapper = ({ blockRef, blockIndex, parentBlockIndex, parentBlockRef, position, onMove, hasChildren, children, className, dndDragType, dndDropType, deep, }) => {
    const wrapperId = useRef(blockRef || getUuid());
    const [dropPosition, setDropPosition] = useState(null);
    const [isHighlighted, setIsHighlighted] = useState(false);
    const ref = useRef(null);
    const { ref: inViewRef, inView } = useInView({ delay: 100 });
    const setRefs = useCallback((node) => {
        ref.current = node;
        inViewRef(node);
    }, [inViewRef]);
    const moveBlock = (blockRef, draggingParentBlockRef) => {
        onMove({
            targetBlockRef: parentBlockRef,
            parentBlockRef: draggingParentBlockRef,
            blockRef: blockRef,
            position: position === 'start' ? 0 : dropPosition === 'top' ? position : position + 1,
        });
    };
    const [{ isDragging }, drag] = useDrag({
        type: dndDragType ? dndDragType : 'disabled',
        item: {
            blockRef: blockRef,
            blockIndex: blockIndex,
            parentBlockIndex: parentBlockIndex,
            parentBlockRef: parentBlockRef,
        },
        collect: (monitor) => ({
            isDragging: dndDragType ? monitor.isDragging() : false,
        }),
    });
    const [isOver, drop] = useDrop({
        accept: dndDropType ? dndDropType : 'disabled',
        hover(item, monitor) {
            if (!ref.current || item.blockRef === blockRef || !dndDropType)
                return;
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            const dropPosition = hoverClientY < hoverMiddleY ? 'top' : 'bottom';
            if (dropPosition === 'top' &&
                parentBlockIndex === item.parentBlockIndex &&
                blockIndex === item.blockIndex + 1) {
                setDropPosition(null);
            }
            else if (dropPosition === 'bottom' &&
                parentBlockIndex === item.parentBlockIndex &&
                blockIndex === item.blockIndex - 1) {
                setDropPosition(null);
            }
            else {
                setDropPosition(dropPosition);
            }
        },
        drop(item) {
            if (!dndDropType) {
                return;
            }
            else if (ref.current && item.blockRef !== blockRef && dropPosition !== null) {
                moveBlock(item.blockRef, item.parentBlockRef);
            }
        },
        collect(monitor) {
            return dndDropType ? monitor.isOver() : false;
        },
    });
    useEffect(() => {
        if (!isOver)
            setDropPosition(null);
    }, [isOver]);
    useLoaderSubscribe(`highlight_block_${blockRef}`, (payload = {}) => __awaiter(void 0, void 0, void 0, function* () {
        setIsHighlighted(true);
        if (payload.type === 'existing') {
            nativeScrollTo(wrapperId.current, { behavior: 'smooth', block: 'center' });
            setTimeout(() => setIsHighlighted(false), 1000);
        }
    }));
    useEffect(() => {
        if (isHighlighted) {
            setTimeout(() => setIsHighlighted(false), 10000);
        }
    }, [inView]);
    drop(ref);
    return (_jsxs("div", Object.assign({ id: wrapperId.current, ref: setRefs, className: classNames(styles.wrapper, className, {
            [styles.wrapperHasChildren]: hasChildren && deep === 0,
            [styles.wrapperHighlighted]: isHighlighted,
            [styles.wrapperDraggable]: !!dndDragType,
            [styles.wrapperDrag]: isDragging,
            [styles.wrapperEmpty]: !children,
            [styles.wrapperDropAreaExtension]: !!children && deep === 1,
        }), onMouseEnter: () => isHighlighted && setIsHighlighted(false) }, { children: [dropPosition === 'top' && _jsx("div", { className: classNames(styles.drop, styles.dropTop) }), dropPosition === 'bottom' && _jsx("div", { className: classNames(styles.drop, styles.dropBottom) }), children ? (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ ref: drag, className: classNames(styles.button, isDragging && styles.buttonActive, deep > 0 && styles.buttonDeep1) }, { children: _jsx(Icon, { type: 'dnd', className: styles.icon }) })), children] })) : null] })));
};
export default observer(BlockOrderWrapper);
